<template>
  <div class="relative cursor-pointer" @click="openProfile">
    <transition v-if="!loadedFromCache" name="opacity">
      <img
        v-if="pictureUrl"
        alt="Profile Picture"
        draggable="false"
        class="h-16 w-16 rounded-lg border-2 border-black-dark object-cover"
        :class="shadow"
        :src="pictureUrl"
      />
      <div
        v-else
        class="h-16 w-16 rounded-lg border-2 border-black-dark bg-gradient-to-br from-orange to-pink shadow-yellow-hard-sm"
      ></div>
    </transition>
    <template v-else>
      <img
        v-if="pictureUrl"
        alt="Profile Picture"
        draggable="false"
        class="h-16 w-16 rounded-lg border-2 border-black-dark object-cover"
        :class="shadow"
        :src="pictureUrl"
      />
      <div
        v-else
        class="h-16 w-16 rounded-lg border-2 border-black-dark bg-gradient-to-br from-orange to-pink shadow-yellow-hard-sm"
      ></div>
    </template>
  </div>
</template>
<script>
import { ImageSizes } from "@/enums/images-sizes";
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { useUserStore } from "@/stores/modules/user/user";
import { useMemberProfile } from "@/composables/useMemberProfile";
import { getObjectPathValue } from "@/helpers/utils/ObjectPath";

export default {
  name: "SmallUserThumb",
  props: {
    uuid: {
      type: String,
      required: true,
    },
    groupId: {
      type: String,
      default: "",
    },
    color: {
      required: false,
      type: String,
      default: "orange",
    },
  },
  setup() {
    const groupsStore = useGroupsStore();
    const userStore = useUserStore();
    const { openMemberProfile } = useMemberProfile();
    return { groupsStore, userStore, openMemberProfile };
  },
  data() {
    return {
      THUMB: ImageSizes.Thumb,
    };
  },
  computed: {
    activeGroupId() {
      return this.userStore.activeGroup;
    },
    targetGroupId() {
      return this.groupId || this.userStore.activeGroup;
    },
    shadow() {
      switch (this.color) {
        case "orange":
          return "shadow-yellow-hard-sm";
        case "green":
          return "shadow-green-hard-sm";
        case "red":
          return "shadow-red-hard-sm";
        default:
          return "shadow-blue-hard-sm";
      }
    },
    membersProfilePicturesData() {
      return this.groupsStore.groupMembersProfilePictureData;
    },
    pictureUrl() {
      return getObjectPathValue(this.membersProfilePicturesData, [
        this.targetGroupId,
        this.uuid,
        ImageSizes.Thumb,
        "url",
      ]);
    },
    loadedFromCache() {
      return (
        getObjectPathValue(this.membersProfilePicturesData, [
          this.targetGroupId,
          this.uuid,
          ImageSizes.Thumb,
          "fromCache",
        ]) ?? false
      );
    },
  },
  watch: {
    uuid: {
      immediate: true,
      handler(newUuid) {
        if (newUuid) {
          this.fetchProfilePicture();
        }
      },
    },
    targetGroupId: {
      handler(newGroupId) {
        if (newGroupId) {
          this.fetchProfilePicture();
        }
      },
    },
  },
  methods: {
    fetchProfilePicture() {
      if (this.uuid && this.targetGroupId && !this.pictureUrl) {
        this.groupsStore.downloadGroupMemberProfilePictureData({
          uuid: this.uuid,
          groupId: this.targetGroupId,
          format: ImageSizes.Thumb,
        });
      }
    },
    openProfile() {
      this.openMemberProfile(this.uuid);
    },
  },
};
</script>
<style scoped lang="scss">
.opacity-enter-active {
  transition: opacity 0.5s ease;
}

.opacity-leave-active {
  position: absolute;
  transition: opacity 0.5s ease;
}
.opacity-enter, .opacity-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}
</style>
